import pdfMake from 'pdfmake';
import { cdate, now } from "../common/utils/CDate";
import { deepEquals, readField } from "../common/utils/objectUtilities";
import { dynamicSort } from "../common/utils/tableUtilities";
import { termMatches } from "../common/utils/glossaryUtilities";
import { CustomFormPrintingFactory } from "../common/utils/customFormPrinting";
import { genDocDefinition } from "../utils/printingUtilities";
import { getDescriptor, realizeJob, realizeJobs } from "../reducers/jobSlice";
import { realizePriorities } from "../reducers/prioritySlice";
import { realizeJobForms } from "../reducers/jobCustomFormSlice";
import { realizeRelatedJobs } from "../reducers/jobRelationSlice";
import { getJobImageData, realizejobImages } from "../reducers/jobImageSlice";
import { realizeJobRelatedDocs } from "../reducers/jobRelatedDocSlice";
import { realizeSite } from "../reducers/siteSlice";
import { realizeLevel } from "../reducers/levelSlice";
import { realizeType } from "../reducers/typeSlice";
import { createNote, realizeJobNotes } from "../reducers/jobNoteSlice";
import { realizeWeather } from "../reducers/weatherSlice";
import { realizeProblem } from "../reducers/problemSlice";
import { realizeJobTags } from "../reducers/jobTagSlice";
import { realizeGlossary } from "../reducers/glossarySlice";
import { generateDocx } from "../common/utils/docxUtilities";
import { withRealizationScope } from '../common/hooks/initHook';
import { deepClone } from "../common/utils/objectUtilities";
import { checkEnabled } from './enableUtilities';
import { realizeJobAccessRequests } from '../reducers/jobAccessRequestsSlice';
import { insertZeroWidthSpacesInParagraph } from '../common/utils/printingUtilities';

export const JobPrinterFactory = function (realizationContext, lang) {
    const { clientConfig: configuration, t } = realizationContext

    // #region helper functions
    let makeCell = (field, obj, prev, defaultValue, format) => {
        let result = { text: defaultValue ?? ' ' };
        if (format) {
            let formatted = format(obj?.[field] ? obj[field] : null);
            if (!formatted) {
                result = { text: defaultValue ?? ' ' };
            } else if (typeof formatted == 'string') {
                result = { text: formatted };
            } else {
                result = formatted;
            }
        } else if (obj?.[field]) {
            result = { text: obj[field] };
        }
        if (prev && (!obj || obj[field] !== prev[field])) {
            result.style = 'red'
        }
        return result;
    }
    // #endregion

    // #region sections
    const makeJobBody = (job, last, priorities) => {
        let jobBody = [];
        const prevJob = readField(last, 'job');
        if (configuration.enableSites || configuration.enableLevels) {
            let row = [];
            if (configuration.enableSites) {
                row.push({ text: t('site', {count: 1}) + ':', style: 'bold' });
                row.push(makeCell('site', job, prevJob));
            }
            if (configuration.enableLevels) {
                row.push({ text: t('level', {count: 1}) + ':', style: 'bold' });
                row.push(makeCell('level', job, prevJob));
            }
            if (!configuration.enableSites || !configuration.enableLevels) {
                row[row.length - 1].colSpan = 3;
                row.push({});
                row.push({});
            }
            jobBody.push(row);
        }
        if (configuration.enableEvents === true) {
            let eventCell = makeCell('event_name', job, prevJob);
            eventCell.colSpan = 3;
            jobBody.push([{ text: t('event', {count: 1}) + ':', style: 'bold' }, eventCell, {}, {}]);
        } else if (configuration.enableEvents === 'freetext') {
            let eventCell = makeCell('event_free', job, prevJob);
            eventCell.colSpan = 3;
            jobBody.push([{ text: t('event', {count: 1}) + ':', style: 'bold'}, eventCell, {}, {}]);
        }
        jobBody = jobBody.concat([
            [
                { text: t('type', {count: 1}) + ' :', style: 'bold' },
                makeCell('type', job, prevJob),
                { text: t('problem', {count: 1}) + ':', style: 'bold' },
                makeCell('problem', job, prevJob)
            ],
            [
                { text: t('priority', {count: 1}) + ':', style: 'bold' },
                makeCell('priority', job, prevJob, null, value => value ? (priorities?.find(p => p.internalid === value)?.text ?? t('unknown')) : ''),
                { text: t('location', {count: 1}) + ':', style: 'bold' },
                makeCell('location', job, prevJob)
            ],
            [
                { text: t('created') + ':', style: 'bold' },
                makeCell('created', job, prevJob, null, value => value ? cdate(value, configuration.tz).dateTimeFormat(configuration.timezones.enable, true) : ''),
                { text: t('updated') + ':', style: 'bold' },
                makeCell('lastupdate', job, prevJob, null, value => value ? cdate(value, configuration.tz).dateTimeFormat(configuration.timezones.enable, true) : '')
            ]
        ]);
        if (configuration.enableBio) {
            jobBody.push([
                { text: t('age') + ':', style: 'bold' },
                makeCell('age', job, prevJob, t('unknown'), value => '' + value),
                { text: t('gender') + ':', style: 'bold' },
                makeCell('gender', job, prevJob, t('unknown'))
            ]);
        }
        return jobBody
    }

    const makeRelatedTbl = (relatedJobs, related, previous) => {
        let relatedTbl = [
            [
                { text: t('job_one'), style: 'tableHeader' },
                { text: t('type', { count: 1 }), style: 'tableHeader' },
                ...(configuration.enableSites ? [{ text: t('site', { count: 1 }), style: 'tableHeader' }] : []),
                ...(configuration.enableLevels ? [{ text: t('level', { count: 1 }), style: 'tableHeader' }] : []),
                { text: t('problem', { count: 1 }), style: 'tableHeader' }
            ]
        ];

        if (relatedJobs && relatedJobs.length > 0) {
            relatedTbl = relatedTbl.concat(relatedJobs.map(function (item, index) {
                let row = [
                    { noWrap: true, text: item.displayid != null ? item.displayid : ' ' },
                    { noWrap: true, text: item.type != null ? item.type : ' ' }
                ];
                if (configuration.enableSites) {
                    row.push({ noWrap: true, text: item.site != null ? item.site : ' ' });
                }
                if (configuration.enableLevels) {
                    row.push({ noWrap: true, text: item.level != null ? item.level : ' ' });
                }
                row.push({ text: item.problem != null ? item.problem : ' ' });

                let relationEntry = related[index];
                let changed = previous && !previous.some(p => p.relatedjobs?.some(prj => prj.relatedid === relationEntry.relatedid && JSON.stringify(prj) === JSON.stringify(relationEntry)));
                if (changed) {
                    row.forEach(cell => cell.style = 'red');
                }
                return row;
            }));
        } else {
            let colCount = 3 + (configuration.enableSites ? 1 : 0) + (configuration.enableLevels ? 1 : 0);
            relatedTbl.push([
                { noWrap: true, colSpan: colCount, text: t('none') },
                ...Array(colCount - 1).fill({})
            ]);
        }
        return relatedTbl;
    };

    const makeJobAccessRequestsTbl = (accessRequests) => {
        let tbl = [
            [
                { text: t('username'), style: 'tableHeader' }, 
                { text: t('type', { count: 1 }), style: 'tableHeader' },
                ...(configuration.enableSites ? [{ text: t('site', { count: 1 }), style: 'tableHeader' }] : []),
                ...(configuration.enableLevels ? [{ text: t('level', { count: 1 }), style: 'tableHeader' }] : []),
                { text: t('status'), style: 'tableHeader' },
                { text: t('decisionTimeStamp'), style: 'tableHeader' },
                { text: t('decisionUsername'), style: 'tableHeader' }
            ]
        ];
        if (accessRequests.length > 0) {
            tbl = tbl.concat(accessRequests.map(req => {
                
                let row = [
                    { noWrap: true, text: req.username || ' ' },
                    { noWrap: true, text: req.assistance.type != null ? req.type : ' ' }
                ];
                if (configuration.enableSites) {
                    row.push({ noWrap: true, text: req.assistance.site != null ? req.site : ' ' });
                }
                if (configuration.enableLevels) {
                    row.push({ noWrap: true, text: req.assistance.level != null ? req.level : ' ' });
                }
                let status
                if (req.accepted_timestamp) {
                    status = t('requestApproved')
                } else if (req.rejected_timestamp) {
                    status = t('requestDenied')
                } else {
                    status = t('pending')
                }
                row.push({ noWrap: true, text: status || ' ' });
                row.push({ text: req.decision_username ? cdate(req.accepted_timestamp || req.rejected_timestamp, configuration.tz).dateTimeFormat(configuration.timezones.enable, true) : ' ' });
                row.push({ noWrap: true, text: req.decision_username || ' ' });
                return row;
            }));
        } else {
            tbl.push([{ noWrap: true, colSpan: 3, text: t('noAccessRequests') }, {}, {}]);
        }
        return tbl;
    };

    const makeDocTbl = (docs, last) => {
        let docTbl = [
            [{ text: t('docType', { count: 1 }), style: 'tableHeader' }, { text: t('details'), style: 'tableHeader' }, { text: t('notes'), style: 'tableHeader' }]
        ];
        if (docs && docs.length > 0) {
            docTbl = docTbl.concat(docs.map(function (item) {
                let row = [
                    { noWrap: true, text: item.doctype != null ? item.doctype : ' ' },
                    { noWrap: true, text: item.details != null ? item.details : ' ' },
                    { text: item.notes != null ? item.notes : ' ' }
                ];
                let changed = last ? last.relateddocs?.every(pi => pi.jdocid !== item.jdocid) || last.relateddocs?.some(pi => pi.jdocid === item.jdocid && JSON.stringify(pi) !== JSON.stringify(item)) : null;
                if (changed) {
                    row.forEach(cell => cell.style = 'red');
                }
                return row;
            }));
        } else {
            docTbl.push([{ noWrap: true, colSpan: 3, text: t('emptyJobRelatedContent') }, {}, {}]);
        }
        return docTbl
    }


    const makeFormTbl = (forms, last) => {
        let formTbl = [
            [{ text: t('customFormID'), style: 'tableHeader' }, { text: t('customFormType'), style: 'tableHeader' }]
        ]
        if (forms?.length > 0) {
            formTbl = formTbl.concat(forms.map(f => {
                let row = [
                    { noWrap: true, text: f.formid.toString() },
                    { noWrap: true, text: f.formtype }
                ]
                let changed = last ? last.forms.every(pf => pf.id !== f.id) || last.forms?.some(pf => pf.id === f.id && JSON.stringify(pf) !== JSON.stringify(f)) : null;
                if (changed) {
                    row.forEach(cell => cell.style = 'red');
                }
                return row;
            }));
        } else {
            formTbl.push([{ noWrap: true, colSpan: 2, text: t('emptyJobForms') }, {}])
        }
        return formTbl;
    }


    const makeImgTbl = (images) => {
        let imgTbl = [];
        let imagesToAdd = images?.filter(img => img?.data || img?.caption);
        if (imagesToAdd?.length > 0) {
            let toPush = [];
            for (let i = 0; i < imagesToAdd.length; ++i) {
                if (toPush.length === 2) {
                    imgTbl.push(toPush);
                    toPush = [];
                }
                let stack = [];
                if (imagesToAdd[i].data) {
                    stack.push({ image: imagesToAdd[i].data, width: 200 });
                }
                if (imagesToAdd[i].caption && !configuration.disableImageCaption) {
                    stack.push({ text: imagesToAdd[i].caption });
                }
                toPush.push({ stack });    
            }
            if (toPush.length > 1) {
                imgTbl.push(toPush);
            } else if (toPush.length > 0) {
                toPush.push({ noWrap: true, text: ' ' });
                imgTbl.push(toPush);
            }
        } else {
            imgTbl.push([{ noWrap: true, colSpan: 2, text: t('noImages')}, {}]);
        }
        return imgTbl;
    }


    let makeNotes = (jobNotes, previous) => {
        let notesTbl = [
            [{ text: t('timeOfDay'), style: 'tableHeader' }, { text: t('user_one'), style: 'tableHeader' }, { text: t('notes'), style: 'tableHeader' }, { text: t('author'), style: 'tableHeader' }]
        ];
        if (jobNotes) {
            let notes = deepClone(jobNotes);
            notes.sort((a, b) => {
                let atimestamp = cdate(a.timestamp);
                let btimestamp = cdate(b.timestamp);
                if (atimestamp.isBefore(btimestamp)) { return -1; }
                if (atimestamp.isAfter(btimestamp)) { return 1; }
                return 0;
            });
            const makeTimestamp = (timestamp, style) => {
                let cstamp = cdate(timestamp, configuration.tz);
                let result = [{ noWrap: true, text: cstamp?.dateFormat(), style: style },
                { noWrap: true, text: cstamp?.timeWithSecondsFormat(configuration.timezones.enable), style: style }]
                return result;
            }
            notesTbl = notesTbl.concat(notes.map(function (item) {
                let style = [];
                if (item.autogenerated === 1 || item.autogenerated === '1' || item.autogenerated === true) {
                    style.push('noteSys');
                }
                if (item.strike_username || item.strike_timestamp) {
                    style.push('noteStrike');
                }
                if (previous && !previous.some(p => p.jobnotes.some(n => deepEquals(n, item)))) {
                    style.push('red');
                }
                let usernameCol = item.username != null && item.user_title != null ?
                    {
                        stack: [
                            { text: item.username, style: style },
                            { text: item.user_title, style: style }
                        ]
                    } : { text: item.username != null ? item.username : ' ', style: style };
                let note = null;
                if (item.strike_username || item.strike_timestamp) {
                    note = {
                        stack: [
                            { text: item.note != null ? insertZeroWidthSpacesInParagraph(item.note, { chunkSize: 30, maxSize: 40 }) : ' ', style: style },
                            { text: t('struckBy', {username: item.strike_username, title: item.strike_user_title, time: cdate(item.strike_timestamp, configuration.tz)?.dateTimeWithSecondsFormat(), reason: item.strike_reason}), style: [] }
                        ]
                    };
                } else {
                    note = { text: item.note != null ? insertZeroWidthSpacesInParagraph(item.note, { chunkSize: 30, maxSize: 40 }) : ' ', style: style };
                }
                return [
                    makeTimestamp(item.timestamp, style),
                    usernameCol,
                    note,
                    { text: item.author != null ? item.author : ' ', style: style }
                ];
            }));
        } else {
            notesTbl.push([{ noWrap: true, colSpan: 4, text: t('notesEmpty') }, {}]);
        }
        return notesTbl;
    }


    const makeUserTimezones = (jobNotes) => {
            const userTimezones = {};
            jobNotes.forEach(n => {
                if (!userTimezones[n.username]) {
                    userTimezones[n.username] = [{ tz: n.tz, timestamp: n.timestamp }];
                } else {
                    let current = userTimezones[n.username][userTimezones[n.username].length - 1];
                    if (current.tz !== n.tz) {
                        userTimezones[n.username].push({ tz: n.tz, timestamp: n.timestamp });
                    }
                }
            });
            let users = Object.keys(userTimezones).filter(u => userTimezones[u].length > 0);
            users.sort();
            return users.map(u => [
                { text: u },
                {
                    stack: [{ text: (userTimezones[u][0].tz ?? configuration.defaultTZ) }].concat(userTimezones[u].slice(1).map(u => ({ text: (u.tz ?? configuration.defaultTZ) + ' (' + cdate(u.timestamp, configuration.tz)?.dateTimeWithSecondsFormat(true) + ')'})))
                }
            ])
        }


        const makeWeatherTbl = (weather) => {
            return [
                [{ text: t('weatherConditions'), style: 'tableHeader' }, { text: weather.condition }, { text: t('temperature'), style: 'tableHeader' }, { text: weather.temperature + t('weatherTemperatureUnits') }],
                [{ text: t('humidity'), style: 'tableHeader' }, { text: weather.humidity + t('humidityUnits') }, { text: t('weatherPressure'), style: 'tableHeader' }, { text: weather.pressure ? (weather.pressure + ' ' + t('weatherPressureUnits')) : t('unknown') }],
                [{ text: t('windSpeed'), style: 'tableHeader' }, { text: (weather.windspeed !== undefined && weather.windspeed !== null) ? weather.windspeed.toFixed(2) + ' ' + t('windSpeedUnits') : t('unknown') }, { text: t('windHeading'), style: 'tableHeader' }, { text: weather.windheading + ' ' + t('windHeadingUnits') }],
                [{ text: t('source'), style: 'tableHeader' }, { text: weather.source, colSpan: 3 }, {}, {}]
            ];
        }


        const makeGlossaryTbl = (jobNotes, glossary) => {
            let glossaryTbl = null;
                let usedTerms = {};
                jobNotes?.forEach(note => {
                    let matches = termMatches(glossary, note.note, configuration.ignoreGlossaryCase);
                    matches.forEach(match => {
                        if (!usedTerms[match.term]) {
                            match.definitions.forEach(def => {
                                usedTerms[match.term] = { term: match.term, definition: def.def };
                            })
                        }
                    });
                });
                let entries = Object.values(usedTerms).flat();
                entries.sort(dynamicSort(['term','definition']));
                if (entries.length > 0) {
                    glossaryTbl = entries.map(e => [
                        { text: e.term },
                        { text: e.definition }
                    ])
                    glossaryTbl.unshift([
                        { text: t('glossaryTerm'), style: 'tableHeader' },
                        { text: t('glossaryDefinition'), style: 'tableHeader' }
                    ])
                }
            return glossaryTbl;
        }
    // #endregion


    const makeJobLog = async (job, site, level, type, problem, priorities, tagMap, relatedJobs, related, docs, forms, images, notes, weather, glossary, previous, accessRequests, options = {}) => {
        const customFormPrinting = CustomFormPrintingFactory(realizationContext, lang, (configuration, content, username) => genDocDefinition(configuration, content, username, { footerText: options?.footerText })); 
        let last = previous && Array.isArray(previous) ? previous[0] : previous;
        let title = previous?.nextseq ?
            { text: t('jobUpdatePrintTitle', {id: job.displayid, seq: previous.nextseq, time: cdate(previous.nexttimestamp, configuration.tz).dateTimeWithSecondsFormat(configuration.timezones.enable, true)}), style: 'header' }
            : { text: t('jobPrintTitle', {id: job.displayid, time: now(configuration.tz).dateTimeWithSecondsFormat(configuration.timezones.enable, true)}), style: 'header' };
        // Job Body
        let toReturn = [
            title,
            {
                table: {
                    widths: ['*', '*', '*', '*'],
                    body: makeJobBody(job, last, priorities),
                }
            },
            { text: ' ' }
        ];
        // General custom fields
        let formSpec = configuration.jobGeneralFormSpec;
        if (configuration.enableSites && formSpec && !Array.isArray(formSpec)) {
            formSpec = formSpec[job.site]
        }
        if (formSpec?.length > 0) {
            for (const section of formSpec) {
                const body = await customFormPrinting.sectionToTable(realizationContext, section, readField(job, 'generalcustomfields'), (field, obj) => (last && (!obj || readField(obj, field) !== readField(readField(last, 'generalcustomfields'), field))) ? {style: 'red'} : undefined);
                if (!body?.length) {
                    continue;
                }
                toReturn.push({
                table: {
                    widths: customFormPrinting.sectionTableWidths(),
                    body
                },
                margin: [0, 5, 0, 0]})
            }
        }
        // Custom fields
        for (const def of [site, level, type, problem]) {
            if (def?.formdef?.length > 0) {
                for (const section of def.formdef) {
                    const body = await customFormPrinting.sectionToTable(realizationContext, section, readField(job, 'customfields'), (field, obj) => (last && (!obj || readField(obj, field) !== readField(readField(last, 'customfields'), field))) ? {style: 'red'} : undefined);
                    if (!body?.length) {
                        continue;
                    }
                    toReturn.push({
                        table: {
                            widths: customFormPrinting.sectionTableWidths(),
                            body
                        },
                        margin: [0, 5, 0, 0]})
                }
            }
        }
        // Job Tags
        if (checkEnabled(configuration, configuration.enableJobTags, job)) {
            toReturn = toReturn.concat([
                { text: t('jobTag', {count: 100}), style: 'subheader'},
                {
                    table: {
                        widths: ['*'],
                        body: [
                            [
                                { text: job.tags?.length > 0 ? job.tags.map(t => tagMap[t]).join(', ') : 'None' }
                            ]
                        ]
                    }
                },
                { text: ' ' }
            ])
        }
        // Job access requests
        if (checkEnabled(configuration, configuration.requestableJobs.enable, job)) {
            toReturn = toReturn.concat([
                { text: t('jobAccessRequests', {id: job.displayid}), style: 'subheader' },
                    {
                        table: {
                            headerrows: 1,
                            widths: ['auto', 'auto', ...(configuration.enableSites ? ['auto'] : []),
                            ...(configuration.enableLevels ? ['auto'] : []), 'auto', 'auto', 'auto'],
                            body: makeJobAccessRequestsTbl(accessRequests)
                        }
                    }
                ]);
            }        
        // Related Jobs, Related Documents, Forms, Log
        toReturn = toReturn.concat([
            { text: t('jobRelations'), style: 'subheader' },
            {
                table: {
                    headerrows: 1,
                    widths: ['auto', 'auto', ...(configuration.enableSites ? ['auto'] : []),
                        ...(configuration.enableLevels ? ['auto'] : []), '*'],
                    body: makeRelatedTbl(relatedJobs, related, previous)
                }
            },
            { text: ' '},
            { text: t('jobRelatedContent'), style: 'subheader' },
            {
                table: {
                    headerrows: 1,
                    widths: ['auto', 'auto', '*'],
                    body: makeDocTbl(docs, last)
                }
            },
            { text: ' '},
            { text: t('form', {count: 100}), style: 'subheader' },
            {
                table: {
                    headerrows: 1,
                    widths: ['auto', '*'],
                    body: makeFormTbl(forms, last)
                }
            },
            { text: ' ' },
            { text: t('log', {count: 1}), style: 'subheader' },
            {
                table: {
                    headerrows: 1,
                    widths: ['auto', 80, 'auto', 80],
                    body: makeNotes(notes, previous)
                }
            }]);

            // User Timezones
            if (configuration.timezones.enable) {
                toReturn = toReturn.concat([
                    { text: t('userTimezones'), style: 'subheader' },
                    {
                        table: {
                            headerrows: 1,
                            widths: ['auto', '*'],
                            body: makeUserTimezones(notes)
                        }
                    },
                ]);
            }
            // Images
            toReturn = toReturn.concat([
            { text: ' ' },
            { text: t('jobImages'), style: 'subheader' },
            {
                table: {
                    headerrows: 0,
                    widths: ['*', '*'],
                    body: makeImgTbl(images),
                },
                layout: 'noBorders'
            }
        ]);

        // Weather
        if (configuration.enableWeather && weather) {
            toReturn.push({ text: t('weather'), style: 'subheader' });
            toReturn.push({ text: ' ' });
            toReturn.push({
                table: {
                    headerrows: 0,
                    widths: ['*', '*', '*', '*'],
                    body: makeWeatherTbl(weather)
                }
            });
        }
        
        // Glossary
        if (configuration.enableGlossary && glossary) {
            let glossaryTbl = makeGlossaryTbl(notes, glossary);
            if (glossaryTbl) {
                toReturn.push({ text: ' ' });
                toReturn.push({ text: t('glossary'), style: 'subheader' });
                toReturn.push({
                    table: {
                        headerrows: 1,
                        widths: ['*', '*'],
                        body: glossaryTbl
                    }
                });
            }
        }
        if (configuration.logSuffix) {
            toReturn.push(configuration.logSuffix);
        }
        return toReturn;
    }

    return {
        makeLog: async (job, site, level, type, problem, priorities, tags, relatedJobs, related, docs, forms, images, notes, weather, glossary, username, previous, accessRequests, options) => {
            let tagMap = {};
            if (tags && tags.length > 0) {
                tags.forEach(t => tagMap[t.id] = t.tag);
            }
            let content = await makeJobLog(job, site, level, type, problem, priorities, tagMap, relatedJobs, related, docs, forms, images, notes, weather, glossary, previous, accessRequests, options);
            return genDocDefinition(configuration, content, username, options)
        }
    }
}

export const printJob = async (i18n, realizationContext, reason, jobid, options = {}) => {
        const { clientConfig, credentials, network, t } = realizationContext;
        const { templateDoc } = options
        if (!reason) {
            return;
        }
        
        return await withRealizationScope(realizationContext, async realizationContext => {
            const job = await realizeJob(realizationContext, jobid);
            const priorities = await realizePriorities(realizationContext);
            const forms = await realizeJobForms(realizationContext, jobid);
            const related = await realizeRelatedJobs(realizationContext, jobid);
            const relatedJobs = await realizeJobs(realizationContext, related.map(r => r.relatedid))
            const imageInfo = await realizejobImages(realizationContext, jobid);
            const docs = await realizeJobRelatedDocs(realizationContext, jobid);
            const site = await realizeSite(realizationContext, job.site);
            const level = await realizeLevel(realizationContext, job.site, job.level);
            const type = await realizeType(realizationContext, job.site, job.level, job.type);
            const notes = await realizeJobNotes(realizationContext, jobid);
            const weather = await realizeWeather(realizationContext, job.created);
            const problem = await realizeProblem(realizationContext, job.site, job.level, job.type, job.problem);
            const tags = await realizeJobTags(realizationContext);
            const glossary = await realizeGlossary(realizationContext);
            const accessRequests = await realizeJobAccessRequests(realizationContext, job.jobid);
            createNote(network, { author: credentials.username, username: credentials.username, jobid, note: t('jobPrintNote', { username: credentials.username, now: now().dateTimeWithSecondsFormat(clientConfig.tz.enable), reason }) });
            const imgData = [];
            for (const img of imageInfo) {
                imgData.push(await getJobImageData(network, jobid, img.filename));
            }
            let images = imageInfo.map((img, idx) => ({...img, data: imgData[idx]}));
            if (!templateDoc) {
                const jobPrinter = JobPrinterFactory(realizationContext, i18n.resolvedLanguage);
                const filename = `${clientConfig.jobTerm}${job.displayid}_log_${now(clientConfig.tz).fileName(clientConfig.timezones.enable)}.pdf`
                const pdf = pdfMake.createPdf(await jobPrinter.makeLog(job, site, level, type, problem, priorities, tags, relatedJobs, related, docs, forms, images, notes.filter(note => !options?.excludeStruckNotes || (!note?.strike_username && !note?.strike_timestamp)), weather, glossary, credentials.username, null, accessRequests, { footerText: options?.excludeStruckNotes ? t('footerExcludedStruckNotes') :  '' }), null, {
                    Roboto: {
                        normal: 'dejavusans.ttf',
                        bold: 'dejavusans-bold.ttf',
                        italics: 'dejavusans-oblique.ttf',
                        bolditalics: 'dejavusans-oblique.ttf'
                    }
                });
                return new Promise((f, r) => pdf.getBlob(blob => f({ blob, filename })));
            } else {
                const doc = await network.fetchData('/customform/templates/' + encodeURIComponent(templateDoc.file));
                const result = await generateDocx(clientConfig, network, {
                    template: await doc.arrayBuffer(),
                    data: {
                        job
                    },
                    noSandbox: true,
                    additionalJsContext: {
                        formatDateTime: function (date) {
                            return date ? cdate(date).dateTimeFormat(clientConfig.timezones.enable) : '';
                        },
                        formatDate: function (date) {
                            return date ? cdate(date).dateFormat(clientConfig.timezones.enable) : '';
                        },
                        formatTime: function (date) {
                            return date ? cdate(date).timeFormat(clientConfig.timezones.enable) : '';
                        },
                        orEmpty: function (data) {
                            return data !== null && data !== undefined ? data : '';
                        },
                        getJobDescriptor: function (jobid) {
                            return getDescriptor(clientConfig.jobListDescriptor, job);
                        },
                        nowDate: function () { return now(clientConfig.tz).dateFormat(clientConfig.timezones.enable) },
                        nowTime: function () { return now(clientConfig.tz).timeFormat(clientConfig.timezones.enable) }
                    }
                });
                return { blob: new Blob([result.data], { type: result.mime }), filename: templateDoc.file + '_' + now(clientConfig.tz).dateTimeFormat() + '.' + result.type };
            }
        })
    }